import api from '@/utils/axios';

function unwrap(response, fallbackMessage) {
  if (!response.data?.success) {
    const error = new Error(response.data?.error?.message || fallbackMessage);
    error.code = response.data?.error?.code || null;
    throw error;
  }
  return response.data.data;
}

async function postManage(route, action, payload = {}) {
  const response = await api.post(route, { action, ...payload });
  return unwrap(response, 'Request failed');
}

const optionsCache = new Map();
const optionsCachePromise = new Map();

const cacheKeyFor = (includeInactive) =>
  includeInactive ? 'with_inactive' : 'active_only';

export function invalidateEmploymentReferenceOptions() {
  optionsCache.clear();
  optionsCachePromise.clear();
}

export async function fetchEmploymentReferenceOptions({
  force = false,
  includeInactive = false,
} = {}) {
  const cacheKey = cacheKeyFor(includeInactive);

  if (!force && optionsCache.has(cacheKey)) {
    return optionsCache.get(cacheKey);
  }
  if (!force && optionsCachePromise.has(cacheKey)) {
    return optionsCachePromise.get(cacheKey);
  }

  const promise = api
    .post('employment-reference/options/get', {
      include_inactive: includeInactive ? 1 : undefined,
    })
    .then((response) => {
      const data = unwrap(response, 'Failed to load employment reference options');
      const normalized = {
        employment_types: Array.isArray(data.employment_types) ? data.employment_types : [],
        companies: Array.isArray(data.companies) ? data.companies : [],
      };
      optionsCache.set(cacheKey, normalized);
      return normalized;
    })
    .finally(() => {
      optionsCachePromise.delete(cacheKey);
    });

  optionsCachePromise.set(cacheKey, promise);
  return promise;
}

export const employmentReferenceApi = {
  types: {
    create(payload) {
      return postManage('employment-reference/types/manage', 'create', payload);
    },
    update(payload) {
      return postManage('employment-reference/types/manage', 'update', payload);
    },
    delete(payload) {
      return postManage('employment-reference/types/manage', 'delete', payload);
    },
    deactivate(payload) {
      return postManage('employment-reference/types/manage', 'update', {
        ...payload,
        is_active: 0,
      });
    },
  },
  companies: {
    create(payload) {
      return postManage('employment-reference/companies/manage', 'create', payload);
    },
    update(payload) {
      return postManage('employment-reference/companies/manage', 'update', payload);
    },
    delete(payload) {
      return postManage('employment-reference/companies/manage', 'delete', payload);
    },
    deactivate(payload) {
      return postManage('employment-reference/companies/manage', 'update', {
        ...payload,
        is_active: 0,
      });
    },
  },
};
